import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  setPersistence,
  updateProfile,
} from "firebase/auth";

import { Banner, Button, PasswordField, TextField, focusFirstInvalid } from "@ui/react";
import { auth } from "../../lib/firebase";
import { VERIFICATION_SEND_FAILED } from "./verificationHandoff";
import { firebaseAuthMessage, isEmailInUse } from "./firebaseErrors";
import { OnboardingLayout } from "./OnboardingLayout";

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
}

export function RegisterScreen() {
  const formRef = useRef<HTMLFormElement>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const next: FieldErrors = {};
    if (!firstName.trim()) next.firstName = "Enter your first name.";
    if (!lastName.trim()) next.lastName = "Enter your last name.";
    if (!email.trim()) next.email = "Enter your email address.";
    else if (!email.includes("@")) next.email = "Enter a valid email address.";
    if (!password) next.password = "Choose a password.";
    else if (password.length < 8) next.password = "Use at least 8 characters.";
    setFieldErrors(next);
    if (Object.keys(next).length) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    setEmailTaken(false);

    try {
      /* Session-scoped, set directly rather than through applyPersistence():
       * that helper also writes the shared remember-me preference, which would
       * leave the login form's checkbox pre-ticked for the next person at this
       * keyboard. Without this line the SDK's default hierarchy wins and a
       * brand-new account is stored durably in IndexedDB — the exact opposite of
       * the shared-machine default this app promises. If the tab dies before
       * verification, signing back in lands on the same gate.
       *
       * Registration is client-side ONLY. There is no public POST /auth/register
       * on the contractor API — it was deleted because it asked the backend to
       * create the very Firebase account the SDK had just created, failing with
       * EMAIL_EXISTS and aborting before it could write anything. The contractor
       * record and the `userType` claim are created exactly once, later, by
       * POST /auth/complete on the profile step. */
      await setPersistence(auth(), browserSessionPersistence);
      const credential = await createUserWithEmailAndPassword(auth(), email.trim(), password);

      // Everything past this point runs while the router is already moving the
      // contractor to the verification gate, so this component is unmounting.
      await updateProfile(credential.user, {
        displayName: `${firstName.trim()} ${lastName.trim()}`.trim(),
      }).catch(() => undefined);

      try {
        await sendEmailVerification(credential.user);
      } catch {
        // Nothing rendered here would survive the navigation, so the failure is
        // handed across it. Otherwise a contractor whose mail never sent (quota
        // exhausted, flaky connection) sits on the verification screen waiting
        // for an email that was never attempted.
        try {
          sessionStorage.setItem(VERIFICATION_SEND_FAILED, "1");
        } catch {
          /* Storage unavailable — they still have the Resend button. */
        }
      }
    } catch (error) {
      if (isEmailInUse(error)) setEmailTaken(true);
      else setFormError(firebaseAuthMessage(error, "We couldn't create your account. Please try again."));
      setSubmitting(false);
    }
  };

  return (
    <OnboardingLayout
      eyebrow="Join the craft"
      title="Create your account."
      lead="Take on work in your trade and earn on your own terms."
      step={1}
      footer={
        <p className="muted">
          Already have an account?{" "}
          <Link className="btn btn-tertiary" to="/login">
            Sign in
          </Link>
        </p>
      }
    >
      <form ref={formRef} className="form" onSubmit={onSubmit} noValidate>
        {emailTaken ? (
          /* Both Shoku apps share ONE Firebase Auth pool and the `userType`
           * claim is single-valued, so an address already used as a homeowner
           * can never also become a contractor. Say so plainly — the mobile
           * app's silent failure here read as "the screen just reloaded". */
          <Banner tone="warn" title="That email is already registered">
            An account already exists with this address — including a Shoku homeowner account,
            which can't be reused for contractor work. Sign in instead, or register with a
            different email.{" "}
            <Link className="btn btn-tertiary" to="/login">
              Sign in instead
            </Link>
          </Banner>
        ) : null}

        {formError ? <Banner tone="danger" title="Sign up failed">{formError}</Banner> : null}

        <div className="field-grid">
          <TextField
            label="First name"
            name="given-name"
            autoComplete="given-name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            error={fieldErrors.firstName ?? null}
            autoFocus
            required
          />
          <TextField
            label="Last name"
            name="family-name"
            autoComplete="family-name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            error={fieldErrors.lastName ?? null}
            required
          />
        </div>

        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          placeholder="you@somewhere.com"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setEmailTaken(false);
          }}
          error={fieldErrors.email ?? null}
          hint="We send a verification link here before you can take work."
          required
        />

        <PasswordField
          label="Password"
          name="new-password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password ?? null}
          hint="At least 8 characters."
          required
        />

        <Button type="submit" size="lg" loading={submitting}>
          Create account
        </Button>
      </form>
    </OnboardingLayout>
  );
}
